import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { every, map, some } from 'lodash'

import { ActionableDialog, NotActionableDialog, Select } from '@/components'
import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import { Table } from '@/components/Table'
import { useAuthStore } from '@/features/auth/authStore'
import { useCommentList, useCommentsStore } from '@/features/comment'
import {
  CommentAction,
  getCommentListColumns,
} from '@/features/comment/columns'
import {
  COMMENT_ACTIONS,
  COMMENT_FILTERS_STORAGE_KEY,
  COMMENT_STATUSES,
  REVIEW_STATUSES,
} from '@/features/comment/constants'
import { useDeliverableSetList } from '@/features/deliverable-set'
import {
  useDocumentList,
  useDocumentsList,
  useDocumentStore,
} from '@/features/document'
import { useProjectList } from '@/features/project'
import { ERoleType } from '@/features/user-management/constants'
import { useDebounce } from '@/hooks/useDebounce'
import { ResponseThread } from '@/schema'
import { SortOrder } from '@/types/common'
import {
  DEFAULT_GET_ALL_LIMIT,
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
  SortDirection,
} from '@/utils/constants'
import {
  buildDeliverableSetDocument,
  buildDeliverableSetRoute,
  CommentNavigationState,
} from '@/utils/routes'

const CommentListPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [deliverableSetSearchText, setDeliverableSetSearchText] = useState('')
  const [tflFileSearchText, setTflFileSearchText] = useState('')
  const [projectSearchText, setProjectSearchText] = useState('')
  const { setOpenDocument, setActiveDocumentId } = useDocumentStore()
  const filtersLoadedRef = useRef(false)
  const [isWaitingForFilterData, setIsWaitingForFilterData] = useState(false)

  const {
    searchText,
    filters,
    page,
    sort,
    setSearchText,
    setFilter,
    setPage,
    setSort,
    reset,
  } = useCommentsStore()

  // Get user roles and project access
  const roles = useAuthStore((state) => state.roles)
  const getListProjectUserHasAccess = useAuthStore(
    (state) => state.getListProjectUserHasAccess
  )

  // Determine if user should have project-scoped access
  const shouldScopeToUserProjects = useMemo(() => {
    const hasProjectLeadRole = roles?.some(
      (role) => role.role_name === ERoleType.PROJECT_LEAD
    )
    const hasTeamMemberRole = roles?.some(
      (role) => role.role_name === ERoleType.TEAM_MEMBER
    )
    const hasOrgAdminRole = roles?.some(
      (role) => role.role_name === ERoleType.ORGANIZATION_ADMIN
    )

    // Apply project scoping if user is Project Lead or Team Member, but NOT Org Admin
    return (hasProjectLeadRole || hasTeamMemberRole) && !hasOrgAdminRole
  }, [roles])

  // Compute the final filters with project scoping if needed
  const computedFilters = useMemo(() => {
    if (shouldScopeToUserProjects) {
      const accessibleProjectIds = getListProjectUserHasAccess()
      return {
        ...filters,
        projectIds: accessibleProjectIds,
      }
    }
    return filters
  }, [filters, shouldScopeToUserProjects, getListProjectUserHasAccess])

  const {
    data: { data: deliverableSetsData = [] } = {},
    isLoading: isLoadingDeliverableSets,
  } = useDeliverableSetList({
    search: deliverableSetSearchText,
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'deliverable_set_name', direction: SortDirection.ASC },
    filters: {
      projectId: filters.projectIds?.[0] || undefined,
    },
  })

  // Fetch projects for the project filter
  const {
    data: { data: projectsData = [] } = {},
    isLoading: isLoadingProjects,
  } = useProjectList({
    search: projectSearchText,
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'project_name', direction: SortDirection.ASC },
  })

  // Use conditional hooks based on deliverable set selection
  const {
    data: { data: filteredTflFilesData = [] } = {},
    isLoading: isLoadingFilteredTflFiles,
  } = useDocumentList({
    deliverableSetId: filters.deliverableSetId || '',
    search: tflFileSearchText,
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
  })

  const {
    data: { data: allTflFilesData = [] } = {},
    isLoading: isLoadingAllTflFiles,
  } = useDocumentsList({
    search: tflFileSearchText,
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
  })

  const tflFilesData = filters.deliverableSetId
    ? filteredTflFilesData
    : allTflFilesData

  const isTflFilesLoading = filters.deliverableSetId
    ? isLoadingFilteredTflFiles
    : isLoadingAllTflFiles

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  useEffect(() => {
    if (filtersLoadedRef.current) {
      return
    }

    let incomingFilters = null

    const storedFilters = localStorage.getItem(COMMENT_FILTERS_STORAGE_KEY)

    if (storedFilters) {
      incomingFilters = JSON.parse(storedFilters)
      localStorage.removeItem(COMMENT_FILTERS_STORAGE_KEY)
    }

    if (!incomingFilters) {
      const navigationState = location.state as CommentNavigationState | null
      incomingFilters = navigationState?.filters
    }

    if (incomingFilters) {
      const newFilters: Partial<typeof filters> = {
        deliverableSetId: incomingFilters.deliverableSetId || null,
        tflFileId: incomingFilters.tflFileId || null,
        status: incomingFilters.status || null,
        reviewStatus: incomingFilters.reviewStatus || null,
        action: incomingFilters.action || null,
        projectIds: incomingFilters.projectId
          ? [incomingFilters.projectId]
          : null,
      }

      setFilter(newFilters)
      globalThis.history.replaceState({}, '', location.pathname)
      filtersLoadedRef.current = true

      // Mark that we're waiting for filter data to load
      if (newFilters.deliverableSetId || newFilters.tflFileId) {
        setIsWaitingForFilterData(true)
      }
    }

    return () => {
      if (!filtersLoadedRef.current) {
        reset()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isWaitingForFilterData) {
      return
    }

    const loadingStates = [
      {
        hasFilter: filters.deliverableSetId,
        isLoading: isLoadingDeliverableSets,
      },
      { hasFilter: filters.tflFileId, isLoading: isTflFilesLoading },
    ]

    const allFiltersLoaded = every(
      loadingStates,
      ({ hasFilter, isLoading }) => !hasFilter || !isLoading
    )

    if (allFiltersLoaded) {
      setIsWaitingForFilterData(false)
    }
  }, [
    isWaitingForFilterData,
    filters.deliverableSetId,
    filters.tflFileId,
    isLoadingDeliverableSets,
    isTflFilesLoading,
  ])

  useEffect(() => {
    setPage(0)
  }, [searchQuery, setPage])

  useEffect(() => {
    setPage(0)
  }, [
    filters.deliverableSetId,
    filters.tflFileId,
    filters.status,
    filters.reviewStatus,
    filters.action,
    filters.projectIds,
    setPage,
  ])

  const {
    data: { data = [], pagination } = {},
    isLoading,
    isFetching,
  } = useCommentList({
    search: searchQuery,
    page,
    sort:
      sort.column && sort.direction
        ? { column: sort.column, direction: sort.direction }
        : undefined,
    filters: computedFilters,
  })

  // Auto-navigate back if current page exceeds total pages
  // This handles cases where items are deleted and current page becomes invalid
  useEffect(() => {
    const totalPages = pagination?.total_pages ?? 1
    if (
      !isLoading &&
      !isFetching &&
      pagination &&
      page >= totalPages &&
      page > 0
    ) {
      setPage(Math.max(0, totalPages - 1))
    }
  }, [pagination, page, isLoading, isFetching, setPage])

  const handleActionClick = useCallback(
    (action: CommentAction, item: ResponseThread) => {
      switch (action) {
        case 'view-deliverable-set':
          if (item.deliverable_set?.id) {
            navigate(buildDeliverableSetRoute(item.deliverable_set.id))
          }
          break
        case 'view-task':
          if (item.teamwork_sync_url) {
            window.open(item.teamwork_sync_url, '_blank')
          }
          break
        default:
          break
      }
    },
    [navigate]
  )

  const handleRowClick = useCallback(
    (row: ResponseThread, event?: React.MouseEvent) => {
      // Prevent row click if user clicked on a button or link
      if (event) {
        const target = event.target as HTMLElement
        if (target.closest('button') || target.closest('a')) {
          return
        }
      }

      if (!row.deliverable_set?.id || !row.document_id || !row.file_name) {
        return
      }

      const documentData = {
        id: row.document_id,
        filename: row.file_name,
      }

      // Update store immediately
      setOpenDocument([documentData])
      setActiveDocumentId(row.document_id)

      // Navigate to document viewer with comment context
      const route = buildDeliverableSetDocument(row.deliverable_set.id)
      navigate(route, {
        state: {
          document: documentData,
          activeDocumentId: row.document_id,
          versionId: row.document_version_id,
          commentId: row.id, // Pass the comment ID to highlight and scroll to it
        },
      })
    },
    [navigate, setActiveDocumentId, setOpenDocument]
  )

  const columns = useMemo(
    () => getCommentListColumns(handleActionClick, t, isFetching),
    [handleActionClick, t, isFetching]
  )

  const handleSortChange = useCallback(
    (colId: keyof ResponseThread | null, dir: SortOrder) => {
      setSort({
        column: colId,
        direction: dir,
      })
    },
    [setSort]
  )

  const noDataMessage = useMemo(() => {
    const hasActiveFilters = some(
      filters,
      (value) => value !== null && value !== undefined
    )

    if (searchQuery || hasActiveFilters) {
      return <EmptySearchResult translationKey="comment" />
    }
    return null
  }, [searchQuery, filters])

  const tflFileOptions = useMemo(
    () =>
      map(tflFilesData, (doc) => ({
        value: doc.document_id,
        label: doc.display_name,
      })),
    [tflFilesData]
  )

  // Extract unique projects - always show all available projects
  const projectOptions = useMemo(() => {
    const uniqueProjectsMap = new Map<string, string>()
    deliverableSetsData.forEach((ds) => {
      if (ds.project_id && ds.project_name) {
        uniqueProjectsMap.set(ds.project_id, ds.project_name)
      }
    })
    // Also add projects from the projects API if available
    projectsData.forEach((project) => {
      if (project.id && project.project_name) {
        uniqueProjectsMap.set(project.id, project.project_name)
      }
    })
    return Array.from(uniqueProjectsMap.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [deliverableSetsData, projectsData])

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex justify-between flex-shrink-0">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('comment.page.title')}
          </h1>
          <p className="m-0 text-md text-muted-foreground">
            {t('comment.page.description')}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap justify-between flex-shrink-0 gap-3">
        <InputSearch
          searchText={searchText}
          className="w-1/5"
          setSearchText={setSearchText}
          placeholder={t('comment.filters.search')}
        />

        <div className="flex flex-wrap gap-3">
          <Select
            key={`project-${filters.projectIds?.[0] || 'all'}`}
            value={filters.projectIds?.[0] || ''}
            className="max-w-50"
            onChange={(value) => {
              const newValue = value === 'all' ? null : [value]
              setFilter({
                projectIds: newValue,
                deliverableSetId: null,
                tflFileId: null,
              })
            }}
            placeholder={t('comment.filters.project')}
            searchable
            onSearchChange={setProjectSearchText}
            isLoading={
              isLoadingProjects ||
              isLoadingDeliverableSets ||
              isWaitingForFilterData
            }
            disabled={isWaitingForFilterData}
            options={[
              { value: 'all', label: t('comment.filters.all') },
              ...projectOptions,
            ]}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />
          <Select
            key={`deliverable-${filters.deliverableSetId}`}
            value={filters.deliverableSetId || ''}
            className="max-w-50"
            onChange={(value) => {
              const newValue = value === 'all' ? null : value
              setFilter({
                deliverableSetId: newValue,
                tflFileId: null,
              })
            }}
            placeholder={t('comment.filters.deliverableSet')}
            searchable
            onSearchChange={setDeliverableSetSearchText}
            isLoading={isLoadingDeliverableSets || isWaitingForFilterData}
            disabled={isWaitingForFilterData}
            options={[
              { value: 'all', label: t('comment.filters.all') },
              ...map(deliverableSetsData, (ds) => ({
                value: ds.id,
                label: ds.deliverable_set_name,
              })),
            ]}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />
          <Select
            key={`tfl-${filters.tflFileId}`}
            value={filters.tflFileId || ''}
            onChange={(value) =>
              setFilter({ tflFileId: value === 'all' ? null : value })
            }
            className="max-w-50"
            placeholder={t('comment.filters.tflFile')}
            searchable
            onSearchChange={setTflFileSearchText}
            isLoading={isTflFilesLoading || isWaitingForFilterData}
            disabled={isWaitingForFilterData}
            options={[
              { value: 'all', label: t('comment.filters.all') },
              ...tflFileOptions,
            ]}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />

          <Select
            key={`status-${filters.status}`}
            value={filters.status || ''}
            onChange={(value) =>
              setFilter({ status: value === 'all' ? null : value })
            }
            placeholder={t('comment.filters.commentStatus')}
            options={[
              { value: 'all', label: t('comment.filters.all') },
              ...map(Object.entries(COMMENT_STATUSES), ([key, label]) => ({
                value: key,
                label,
              })),
            ]}
            className="max-w-[160px]"
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />

          <Select
            value={filters.reviewStatus || ''}
            onChange={(value) => {
              const newValue = value === 'all' ? null : value
              setFilter({
                reviewStatus: newValue,
                action: newValue === 'not_reviewed' ? null : filters.action,
              })
            }}
            className="max-w-[160px]"
            placeholder={t('comment.filters.reviewStatus')}
            options={[
              { value: 'all', label: t('comment.filters.all') },
              ...map(Object.entries(REVIEW_STATUSES), ([key, label]) => ({
                value: key,
                label,
              })),
            ]}
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />
          <Select
            value={filters.action || ''}
            onChange={(value) =>
              setFilter({ action: value === 'all' ? null : value })
            }
            placeholder={t('comment.filters.action')}
            disabled={filters.reviewStatus === 'not_reviewed'}
            options={[
              { value: 'all', label: t('comment.filters.all') },
              ...map(Object.entries(COMMENT_ACTIONS), ([key, label]) => ({
                value: key,
                label,
              })),
            ]}
            className="max-w-[160px]"
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <Table
          columns={columns}
          data={data ?? []}
          page={(pagination?.page ?? DEFAULT_PAGE) - 1}
          totalPages={pagination?.total_pages ?? 0}
          onPageChange={setPage}
          isLoading={isLoading || isWaitingForFilterData}
          noDataMessage={noDataMessage}
          orderBy={sort.column as keyof ResponseThread}
          order={sort.direction}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
          roundedTop
        />
      </div>

      {/* Global dialog components */}
      <ActionableDialog />
      <NotActionableDialog />
    </div>
  )
}

export default CommentListPage
